/**
 * Shared utilities: tokenization, stopwords, cosine similarity, normalization.
 *
 * Pure functions, no dependencies. These are the primitives every scorer
 * builds on — if you need something here that isn't, add it here instead
 * of duplicating in a scorer.
 */

// ---------------------------------------------------------------------------
// Stopwords — conservative English list from NLTK minus the words that are
// actually meaningful in resume/JD context ("all", "any", "down", "off",
// "out", "up", "most", "own", "same", "so"). These carry signal.
// ---------------------------------------------------------------------------
export const STOPWORDS: ReadonlySet<string> = new Set([
  "a", "about", "above", "after", "again", "against", "am", "an", "and",
  "are", "as", "at", "be", "because", "been", "before", "being", "below",
  "between", "both", "but", "by", "can", "did", "do", "does", "doing",
  "during", "each", "few", "for", "from", "further", "had", "has", "have",
  "having", "he", "her", "here", "hers", "herself", "him", "himself",
  "his", "how", "i", "if", "in", "into", "is", "it", "its", "itself",
  "me", "more", "my", "myself", "no", "nor", "not", "now", "of", "on",
  "once", "only", "or", "other", "our", "ours", "ourselves", "over",
  "she", "should", "some", "such", "than", "that", "the", "their",
  "theirs", "them", "themselves", "then", "there", "these", "they",
  "this", "those", "through", "to", "too", "under", "until", "very",
  "was", "we", "were", "what", "when", "where", "which", "while", "who",
  "whom", "why", "will", "with", "you", "your", "yours", "yourself",
  "yourselves",
]);

// Words that are ALWAYS meaningful in resume/JD context even if they'd
// normally be stopped by a generic stoplist. We keep these at index time.
const ALWAYS_KEEP: ReadonlySet<string> = new Set([
  "ai", "ml", "ui", "ux", "qa", "io", "os", "db", "dx",
]);

/**
 * Normalize text for keyword scoring:
 *   lowercase → replace common punctuation with spaces → collapse whitespace
 *
 * Preserves alphanumerics, `+`, `#`, `.` inside words (so "C++", "C#", and
 * "Node.js" survive), and `/` between words.
 */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s+#./-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Tokenize normalized text into content words.
 *
 * - Drops stopwords (unless ALWAYS_KEEP)
 * - Drops pure punctuation and 1-char tokens (except ALWAYS_KEEP)
 * - Keeps compound tech tokens intact ("c++", "node.js", "c#")
 */
export function tokenize(text: string): string[] {
  return normalize(text)
    .split(/\s+/)
    .filter((t) => {
      if (!t) return false;
      if (ALWAYS_KEEP.has(t)) return true;
      if (STOPWORDS.has(t)) return false;
      if (t.length < 2) return false;
      // pure punctuation
      if (/^[^a-z0-9]+$/.test(t)) return false;
      return true;
    });
}

/**
 * Stem-aware tokenizer — same as tokenize() but applies the Porter stemmer
 * to each token. Used by the keyword signal so "engineer" and "engineering"
 * collapse to the same stem and don't get counted as independent terms.
 *
 * Compound tech tokens with punctuation (c++, c#, node.js) are passed
 * through without stemming — the Porter rules don't apply to them.
 */
export function tokenizeStemmed(text: string): string[] {
  return tokenize(text).map((t) => {
    if (ALWAYS_KEEP.has(t)) return t;
    if (/[+#./]/.test(t)) return t; // keep compound tech tokens intact
    return porterStem(t);
  });
}

/**
 * Build an n-gram set from tokens. Used by bullet and section scoring to
 * catch multi-word phrases ("machine learning", "software engineer").
 */
export function ngrams(tokens: string[], n: number): string[] {
  if (n <= 1) return tokens;
  const result: string[] = [];
  for (let i = 0; i <= tokens.length - n; i++) {
    result.push(tokens.slice(i, i + n).join(" "));
  }
  return result;
}

// ---------------------------------------------------------------------------
// Vector math
// ---------------------------------------------------------------------------

/** Cosine similarity in [-1, 1]. Returns 0 for zero-norm vectors. */
export function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length) {
    throw new Error(
      `cosine: mismatched dimensions (${a.length} vs ${b.length})`,
    );
  }
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i++) {
    const ai = a[i] ?? 0;
    const bi = b[i] ?? 0;
    dot += ai * bi;
    normA += ai * ai;
    normB += bi * bi;
  }
  if (normA === 0 || normB === 0) return 0;
  return dot / (Math.sqrt(normA) * Math.sqrt(normB));
}

/** Clamp into [0, 1]. Cosine on normalized vectors is already in [0, 1]
 *  for resume text (no antonyms), but we clamp defensively. */
export function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

// ---------------------------------------------------------------------------
// Text hashing — used by the embedding cache. Keep this dependency-free;
// @xenova/transformers is heavy enough without also pulling in a crypto lib.
// FNV-1a 32-bit hash, good enough for a cache key and deterministic.
// ---------------------------------------------------------------------------
export function hashText(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // Return as 8-char unsigned hex
  return (h >>> 0).toString(16).padStart(8, "0");
}

// ---------------------------------------------------------------------------
// Porter stemmer — classic Porter (1980) algorithm, pure-TS implementation.
//
// Added Day 8.5 to collapse morphological variants in the keyword scorer so
// "engineer"/"engineering"/"engineered"/"engineers" all map to "engineer",
// "develop"/"developer"/"developing"/"development" all map to "develop",
// etc. Handles ~95% of common English inflections. Not a full lemmatizer
// (which would need a dictionary) but close enough for resume/JD scoring
// and dependency-free.
//
// Reference: Porter, M.F. (1980), "An algorithm for suffix stripping",
// Program, Vol. 14, no. 3, pp. 130-137.
// ---------------------------------------------------------------------------

const PORTER_VOWELS = new Set(["a", "e", "i", "o", "u"]);

function isConsonant(word: string, i: number): boolean {
  const ch = word[i];
  if (ch === undefined) return false;
  if (PORTER_VOWELS.has(ch)) return false;
  if (ch === "y") {
    // y is a consonant unless preceded by another consonant
    if (i === 0) return true;
    return !isConsonant(word, i - 1);
  }
  return true;
}

/** Measure: number of (vowel_seq)(consonant_seq) pairs in the stem. */
function porterMeasure(stem: string): number {
  let m = 0;
  let i = 0;
  const n = stem.length;
  // Skip initial consonants
  while (i < n && isConsonant(stem, i)) i++;
  while (i < n) {
    // Skip vowels
    while (i < n && !isConsonant(stem, i)) i++;
    if (i < n) {
      m++;
      // Skip consonants
      while (i < n && isConsonant(stem, i)) i++;
    }
  }
  return m;
}

function stemHasVowel(stem: string): boolean {
  for (let i = 0; i < stem.length; i++) {
    if (!isConsonant(stem, i)) return true;
  }
  return false;
}

function endsWithDoubleConsonant(stem: string): boolean {
  const n = stem.length;
  if (n < 2) return false;
  if (stem[n - 1] !== stem[n - 2]) return false;
  return isConsonant(stem, n - 1);
}

function endsWithCvc(stem: string): boolean {
  const n = stem.length;
  if (n < 3) return false;
  if (!isConsonant(stem, n - 3)) return false;
  if (isConsonant(stem, n - 2)) return false;
  if (!isConsonant(stem, n - 1)) return false;
  const last = stem[n - 1];
  if (last === "w" || last === "x" || last === "y") return false;
  return true;
}

/** Classic Porter stemmer, rules 1a through 5b. */
export function porterStem(word: string): string {
  let w = word;
  if (w.length <= 2) return w;

  // Step 1a
  if (w.endsWith("sses")) w = w.slice(0, -2);
  else if (w.endsWith("ies")) w = w.slice(0, -2);
  else if (w.endsWith("ss")) {
    /* unchanged */
  } else if (w.endsWith("s")) w = w.slice(0, -1);

  // Step 1b
  let step1bHit = false;
  if (w.endsWith("eed")) {
    if (porterMeasure(w.slice(0, -3)) > 0) w = w.slice(0, -1);
  } else if (w.endsWith("ed") && stemHasVowel(w.slice(0, -2))) {
    w = w.slice(0, -2);
    step1bHit = true;
  } else if (w.endsWith("ing") && stemHasVowel(w.slice(0, -3))) {
    w = w.slice(0, -3);
    step1bHit = true;
  }
  if (step1bHit) {
    if (w.endsWith("at") || w.endsWith("bl") || w.endsWith("iz")) {
      w = w + "e";
    } else if (endsWithDoubleConsonant(w) && !/[lsz]$/.test(w)) {
      w = w.slice(0, -1);
    } else if (porterMeasure(w) === 1 && endsWithCvc(w)) {
      w = w + "e";
    }
  }

  // Step 1c
  if (w.endsWith("y") && stemHasVowel(w.slice(0, -1))) {
    w = w.slice(0, -1) + "i";
  }

  // Step 2 — suffix substitutions where measure > 0
  const step2: Array<[string, string]> = [
    ["ational", "ate"],
    ["tional", "tion"],
    ["enci", "ence"],
    ["anci", "ance"],
    ["izer", "ize"],
    ["abli", "able"],
    ["alli", "al"],
    ["entli", "ent"],
    ["eli", "e"],
    ["ousli", "ous"],
    ["ization", "ize"],
    ["ation", "ate"],
    ["ator", "ate"],
    ["alism", "al"],
    ["iveness", "ive"],
    ["fulness", "ful"],
    ["ousness", "ous"],
    ["aliti", "al"],
    ["iviti", "ive"],
    ["biliti", "ble"],
  ];
  for (const [from, to] of step2) {
    if (w.endsWith(from)) {
      const stem = w.slice(0, -from.length);
      if (porterMeasure(stem) > 0) w = stem + to;
      break;
    }
  }

  // Step 3
  const step3: Array<[string, string]> = [
    ["icate", "ic"],
    ["ative", ""],
    ["alize", "al"],
    ["iciti", "ic"],
    ["ical", "ic"],
    ["ful", ""],
    ["ness", ""],
  ];
  for (const [from, to] of step3) {
    if (w.endsWith(from)) {
      const stem = w.slice(0, -from.length);
      if (porterMeasure(stem) > 0) w = stem + to;
      break;
    }
  }

  // Step 4 — suffix removal where measure > 1
  const step4 = [
    "al", "ance", "ence", "er", "ic", "able", "ible", "ant", "ement",
    "ment", "ent", "ou", "ism", "ate", "iti", "ous", "ive", "ize",
  ];
  for (const suf of step4) {
    if (w.endsWith(suf)) {
      const stem = w.slice(0, -suf.length);
      if (porterMeasure(stem) > 1) {
        // Special case: "ion" allowed only after s or t
        w = stem;
      }
      break;
    }
  }
  if (w.endsWith("ion")) {
    const stem = w.slice(0, -3);
    if (porterMeasure(stem) > 1) {
      const last = stem[stem.length - 1];
      if (last === "s" || last === "t") w = stem;
    }
  }

  // Step 5a
  if (w.endsWith("e")) {
    const stem = w.slice(0, -1);
    const m = porterMeasure(stem);
    if (m > 1) w = stem;
    else if (m === 1 && !endsWithCvc(stem)) w = stem;
  }

  // Step 5b
  if (porterMeasure(w) > 1 && endsWithDoubleConsonant(w) && w.endsWith("l")) {
    w = w.slice(0, -1);
  }

  return w;
}
